import * as React from 'react';
import { theme } from 'antd';
import './ListLayout.css';

export type ListLayoutSize = 'default' | 'small' | 'large';

interface ListLayoutContextValue {
    size: ListLayoutSize;
    vertical: boolean;
}

const ListLayoutContext = React.createContext<ListLayoutContextValue>({
    size: 'default',
    vertical: false,
});

export interface ListLayoutProps {
    size?: ListLayoutSize;
    itemLayout?: 'horizontal' | 'vertical';
    split?: boolean;
    style?: React.CSSProperties;
    className?: string;
    children: React.ReactNode;
}

export interface ListLayoutItemProps {
    style?: React.CSSProperties;
    className?: string;
    children: React.ReactNode;
    onMouseEnter?: React.MouseEventHandler<HTMLLIElement>;
    onMouseLeave?: React.MouseEventHandler<HTMLLIElement>;
}

function getItemPadding(
    token: ReturnType<typeof theme.useToken>['token'],
    size: ListLayoutSize
): string {
    switch (size) {
        case 'small':
            return `${token.paddingContentVerticalSM}px ${token.paddingContentHorizontal}px`;
        case 'large':
            return `${token.paddingContentVerticalLG}px ${token.paddingContentHorizontalLG}px`;
        default:
            return `${token.paddingContentVertical}px 0`;
    }
}

export function ListLayout({
    size = 'default',
    itemLayout = 'horizontal',
    split = true,
    style,
    className,
    children,
}: ListLayoutProps) {
    const { token } = theme.useToken();
    const context = React.useMemo(
        () => ({
            size,
            vertical: itemLayout === 'vertical',
        }),
        [size, itemLayout]
    );

    const classNames = [
        'list-layout',
        split ? 'list-layout--split' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <ListLayoutContext.Provider value={context}>
            <ul
                className={classNames}
                style={{
                    ...style,
                    ...(split
                        ? ({
                              ['--list-layout-split-border' as string]: `${token.lineWidth}px ${token.lineType} ${token.colorSplit}`,
                          } as React.CSSProperties)
                        : undefined),
                }}
            >
                {children}
            </ul>
        </ListLayoutContext.Provider>
    );
}

export function ListLayoutItem({
    style,
    className,
    children,
    onMouseEnter,
    onMouseLeave,
}: ListLayoutItemProps) {
    const { token } = theme.useToken();
    const { size } = React.useContext(ListLayoutContext);

    return (
        <li
            className={['list-layout__item', className].filter(Boolean).join(' ')}
            style={{
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'space-between',
                width: '100%',
                padding: getItemPadding(token, size),
                color: token.colorText,
                ...style,
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {children}
        </li>
    );
}
